import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchUserCategories,
  fetchUserContents,
  handleApiError,
} from '../../../utils/api';
import { sessionPath } from '../your-progress/progressData';
import { contentsForCategory } from './categoryMeta';
import { HOME_PATH, MEET_EXPERT_PATH } from './constants';
import { withCompletedLast } from './trackCardUtils';

export default function useYourQuickRichuels() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [categories, setCategories] = useState([]);
  const [contents, setContents] = useState([]);
  const [recommendedCategory, setRecommendedCategory] = useState('');

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const ac = new AbortController();
    let ignore = false;

    const run = async () => {
      setLoading(true);
      setLoadError(false);

      try {
        const [categoryResult, contentResult] = await Promise.all([
          fetchUserCategories(navigateRef.current, ac.signal),
          fetchUserContents(navigateRef.current, { signal: ac.signal }),
        ]);
        if (ignore || ac.signal.aborted) return;
        if (!categoryResult || !contentResult) {
          setLoadError(true);
          setCategories([]);
          setContents([]);
          return;
        }
        setCategories(Array.isArray(categoryResult.categories) ? categoryResult.categories : []);
        setRecommendedCategory(categoryResult.recommended_category || '');
        setContents(Array.isArray(contentResult.contents) ? contentResult.contents : []);
      } catch (e) {
        if (ignore || e?.name === 'AbortError' || ac.signal.aborted) return;
        handleApiError(e, navigateRef.current, false);
        setLoadError(true);
        setCategories([]);
        setContents([]);
      } finally {
        if (!ignore && !ac.signal.aborted) setLoading(false);
      }
    };

    run();
    return () => {
      ignore = true;
      ac.abort();
    };
  }, [reloadKey]);

  const recommendedContents = useMemo(() => {
    const matched = contentsForCategory(contents, recommendedCategory);
    const list = matched.length > 0 ? matched : contents;
    return withCompletedLast(list);
  }, [contents, recommendedCategory]);

  const handleSelectTrack = useCallback((item) => {
    const contentId = String(item?.id ?? '').trim();
    if (!contentId) return;
    navigate(sessionPath(contentId));
  }, [navigate]);

  const handleSeeWhatsNext = useCallback(() => {
    document.getElementById('recommended-tracks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleGoHome = useCallback(() => {
    navigate(HOME_PATH);
  }, [navigate]);

  const handleMeetExpert = useCallback(() => {
    navigate(MEET_EXPERT_PATH);
  }, [navigate]);

  const handleRetry = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return {
    loading,
    loadError,
    categories,
    contents,
    recommendedContents,
    recommendedCategory,
    handleSelectTrack,
    handleSeeWhatsNext,
    handleGoHome,
    handleMeetExpert,
    handleRetry,
  };
}
